import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
    useCreateAdminConversationFormQuery,
    useUpdateAdminConversationFormQuery,
} from '@entities/AdminConversationCategory';
import { IForm } from '@entities/AdminConversationCategory/model/types';
import { TAttachmentDTOCamelCased, TAttachmentOnClient } from '@entities/attachment/model/types';
import { getUpsertModalFormData } from '@features/AdminConversationCategoryUpsertModal/lib/helpers/getUpsertModalFormData';
import { useFormHandler } from '@features/AdminConversationCategoryUpsertModal/lib/hooks/useFormHandler';
import { DnDImageList } from '@features/AdminConversationCategoryUpsertModal/ui/DnDImageList';
import { CustomModal, DnD, Flex, Input, TextArea, TextEditor, UiButton } from '@shared/ui';

import cls from './SurveyStep.module.css';

interface SurveyStepProps {
    classes?: string;
    categoryId: number;
    formData?: IForm;
    onClose: () => void;
    formId?: number | null;
}

export const SurveyStep = ({ classes, categoryId, formData, onClose, formId }: SurveyStepProps) => {
    const { t } = useTranslation();

    const { form, fileInputRef, onInputChange, uploadFilesHandler, onClickInputRef } =
        useFormHandler(formData);

    const { mutateAsync: mutateAsyncCreateAdminConversationForm } =
        useCreateAdminConversationFormQuery();

    const { mutateAsync: mutateAsyncUpdateAdminConversationForm } =
        useUpdateAdminConversationFormQuery();

    const createNewsletter = async () => {
        const fd = getUpsertModalFormData(categoryId, form, 'survey', !!formId, []);

        if (!formId) {
            await mutateAsyncCreateAdminConversationForm(fd);
        } else {
            await mutateAsyncUpdateAdminConversationForm({ formData: fd, id: formId });
        }
        onClose();
    };

    const isDisabled = !form.title || !form.link;

    return (
        <>
            <CustomModal.Header classes={cn(cls.SurveyStep, [classes])}>
                {t('SurveyStep.return.CustomModal.Header')}
            </CustomModal.Header>
            <CustomModal.Body classes={cn(cls.SurveyStepBody, [classes])}>
                <Flex gap={12} direction="column">
                    <TextArea
                        maxLength={100}
                        placeholder={t('SurveyStep.return.CustomModal.Body.TextArea.placeholder')}
                        classes={cls.TextArea}
                        inputName="title"
                        onValueChange={onInputChange}
                        value={form.title}
                    />
                    <DnD<TAttachmentOnClient | TAttachmentDTOCamelCased>
                        attachmentsData={form.attachment}
                        multiple={false}
                        onUploadHandler={uploadFilesHandler}
                        fileInputRef={fileInputRef}
                        accept=".png, .jpeg, .gif, .jpg, .heic, .webp"
                        containerClassName={cls.dnd}
                        addFileTitle={t('NewsletterStep.return.CustomModal.Body.DnD.addFileTitle')}
                        renderAttachmentsList={() => (
                            <DnDImageList
                                onClickInputRef={onClickInputRef}
                                file={form.attachment[0]}
                            />
                        )}
                    />
                    <Input
                        name="link"
                        onValueChange={onInputChange}
                        classes={[cls.input]}
                        value={form.link}
                        placeholder={t('SurveyStep.return.CustomModal.Body.Input.placeholder')}
                    />
                    <TextArea
                        maxLength={500}
                        placeholder={t(
                            'SurveyStep.return.CustomModal.Body.TextArea.placeholder.comment'
                        )}
                        classes={cn(cls.TextArea, cls.description)}
                        inputName="comment"
                        onValueChange={onInputChange}
                        value={form.comment}
                    />
                </Flex>
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.footer}>
                <UiButton
                    onClick={onClose}
                    btnColor="secondary"
                    btnText={t('button.cancel.text')}
                />
                <UiButton
                    disabled={isDisabled}
                    onClick={createNewsletter}
                    btnText={
                        formId
                            ? t('SurveyStep.return.CustomModal.Footer.UiButton.btnText.edit')
                            : t('SurveyStep.return.CustomModal.Footer.UiButton.btnText')
                    }
                />
            </CustomModal.Footer>
        </>
    );
};
